import { useEffect, useState } from "react";

const LS_KEY = "bd:agenda:selectedDentists";

/**
 * Ids de profissionais selecionados no filtro da agenda. Persiste em
 * localStorage. Se nunca selecionado, retorna null → o componente que
 * usa o hook decide o default (tipicamente o user atual).
 */
function readStored(): string[] | null {
  if (typeof window === "undefined") return null;
  try {
    const raw = localStorage.getItem(LS_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (Array.isArray(parsed) && parsed.every((x) => typeof x === "string")) {
      return parsed;
    }
  } catch {
    // noop
  }
  return null;
}

function writeStored(ids: string[]): void {
  if (typeof window === "undefined") return;
  try {
    localStorage.setItem(LS_KEY, JSON.stringify(ids));
  } catch {
    // noop
  }
}

export function useSelectedDentists(fallback: string[]): {
  ids: string[];
  setIds: (next: string[]) => void;
  toggle: (id: string) => void;
  selectAll: (allIds: string[]) => void;
  clear: () => void;
} {
  const [ids, setIdsState] = useState<string[]>(
    () => readStored() ?? fallback,
  );

  // Se o fallback mudar (ex: user logou e quer default = seu id), aplica
  // APENAS se nunca foi persistido antes.
  useEffect(() => {
    if (readStored() === null && fallback.length > 0) {
      setIdsState(fallback);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fallback.join(",")]);

  const setIds = (next: string[]) => {
    writeStored(next);
    setIdsState(next);
  };

  const toggle = (id: string) => {
    setIds(ids.includes(id) ? ids.filter((x) => x !== id) : [...ids, id]);
  };

  const selectAll = (allIds: string[]) => setIds(allIds);
  const clear = () => setIds([]);

  return { ids, setIds, toggle, selectAll, clear };
}

/** Retorna cor configurada pro dentista; senão cai em paleta hash. */
const FALLBACK_COLORS = [
  "#14b8a6", // teal
  "#0ea5e9", // sky
  "#10b981", // emerald
  "#f59e0b", // amber
  "#ec4899", // pink
  "#06b6d4", // cyan
  "#84cc16", // lima
  "#ef4444", // red
];

export function getDentistColor(
  userId: string,
  dentistSettings: Record<string, { color?: string; title?: string }>,
  fallback?: string,
): string {
  const custom = dentistSettings[userId]?.color;
  if (custom) return custom;
  if (fallback) return fallback;
  let h = 0;
  for (let i = 0; i < userId.length; i++) h = (h << 5) - h + userId.charCodeAt(i);
  return FALLBACK_COLORS[Math.abs(h) % FALLBACK_COLORS.length];
}

export function getDentistTitle(
  userId: string,
  dentistSettings: Record<string, { color?: string; title?: string }>,
): string {
  return dentistSettings[userId]?.title ?? "Dr.";
}
